import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import type { StudentTabId } from 'core/utils/studentAlerts';
import { UserRole } from 'types';

import {
  PAYMENT_ACCESS_ROLES,
  STUDENT_CARD_ROLES,
  FINANCE_VIEW_ROLES,
  STUDENT_ROLES,
} from '../Palette/paletteRoles';
import {
  STUDENT_HOMEWORK_ROLES,
  INVOICE_LINK_ROLES,
  STUDENT_INVOICES_ROLES,
  STUDENT_PANDADOC_ROLES,
  STUDENT_STAFF_FIELDS_ROLES,
  STUDENT_SURVEY_RESPONSES_ROLES,
} from '../Palette/studentCardRoles';

import { AI_PORTRAIT_ROLES } from '../AiAgents/useAiAgentPermissions';
import {
  AGREEMENT_CORRECT_ROLES,
  ENROLLMENT_UNDO_ROLES,
  hasAnyCorrectionRole,
} from './agreementCorrectionModel';

/**
 * Capability booleans for the Student surfaces (list + card), derived from the
 * current user's roles. Mirrors `useGroupPermissions`. Hiding a control is a UX
 * affordance only — the backend remains the authority on each mutation/export.
 *
 * 🔴 Флаг ЧТЕНИЯ (`canRead*` / `canSee*Tab`) — не косметика. Методисту
 * карточка открыта урезанной (решение владельца 07.10.2026): поля денег и
 * контактов сервер отдаёт ему пустыми, а денежные, документные и
 * коммуникационные корни — отказом. Каждый такой флаг — зеркало ОДНОГО
 * списка бэкенда (`Palette/studentCardRoles`), и им одновременно прячут блок
 * и выключают (`skip`) его запрос: запрос, которого роль не прочтёт, не
 * уходит вовсе.
 */
export interface IStudentPermissions {
  /** Admin, FinanceManager, AccountManager (SalesManager: read). */
  canViewFinance: boolean;
  /** Same set as `canViewFinance` — gates ALL export controls (Req 21.5). */
  canExportFinance: boolean;
  /** Admin, FinanceManager, AccountManager. */
  canRecordPayment: boolean;
  /**
   * Link an EXISTING Fakturownia invoice to a взнос. Admin, FinanceManager,
   * AccountManager. Creates nothing.
   */
  canIssueInvoice: boolean;
  /**
   * «Привязать счёт» / «Привязать к взносу» — `linkInvoiceToContribution`
   * (`INVOICE_LINK_ROLES` = роли мутации: Admin, Finance, Sales, AM). Одно
   * право на кнопку шапки и на действие строки таблиц счетов (E1-3).
   */
  canLinkInvoice: boolean;
  /**
   * Issue a REAL Fakturownia VAT document («Выставить фактуру»). Same role
   * set as `canIssueInvoice`, but kept as its own capability because this is
   * the only student-card action that creates an irreversible fiscal document
   * — so it can be narrowed (or flag-gated) without touching the linking
   * flow. The backend is the authority (`FAKTUROWNIA_ACCESS_ROLES`).
   */
  canIssueFaktura: boolean;
  /** Admin, AccountManager, SalesManager. */
  canAddAgreement: boolean;
  /** Admin, AccountManager, SalesManager. */
  canChangeGroup: boolean;
  /** Admin, AccountManager, SalesManager. */
  canContactEmail: boolean;
  /** Admin, FinanceManager, AccountManager, SalesManager — block LMS access. */
  canBlockLmsAccess: boolean;
  /**
   * Admin, AccountManager, Methodist, HRManager — read this student's survey
   * answers (`STUDENT_SURVEY_RESPONSES_ROLES`). Narrower than the card itself:
   * the tab pairs a named student with their verbatim opinion of a named
   * teacher, so Sales and Finance can open the card but must not see the tab
   * at all.
   */
  canReadStudentSurveys: boolean;
  /**
   * ERP-110 «Исправить группу (ошибка ввода)» — Admin, AccountManager,
   * SalesManager, FinanceManager (роли перевода `changeAgreementGroup`).
   */
  canCorrectAgreementGroup: boolean;
  /** ERP-110 «Отменить зачисление» — Admin, FinanceManager, AccountManager. */
  canUndoEnrollment: boolean;
  /**
   * Есть ли у роли список студентов (`/students`, `STUDENT_ROLES`). Методисту
   * карточка открыта, а список — нет: «К списку студентов» вела бы на 404.
   */
  canOpenStudentList: boolean;

  /* ---------- чтение блоков (флаг = блок виден И запрос уходит) ---------- */

  /**
   * Деньги студента — договоры, взносы, платежи, итоги (полоса KPI), долги,
   * «Требует внимания», выгрузка финансов (`STUDENT_STAFF_FIELDS_ROLES` =
   * `MANAGEMENT_ROLES`). Методисту поля приходят пустыми — блоки по ним
   * сказали бы «договоров нет», «долгов нет».
   */
  canReadMoney: boolean;
  /** Контакты — email и телефон (`CustomUserType`, `MANAGEMENT_ROLES`). */
  canReadContacts: boolean;
  /**
   * «Откуда пришёл» — лиды Kommo и источник привлечения. Панель — ролям
   * `MANAGEMENT_ROLES`: методисту карточка «про учёбу» (решение владельца
   * 07.10.2026), хотя источник привлечения сервер отдаёт любой роли ERP.
   */
  canReadOrigin: boolean;
  /** Служебная лента событий (`timeline`, `MANAGEMENT_ROLES`). */
  canReadTimeline: boolean;
  /** Фактуры (`fakturowniaInvoices`, `FAKTUROWNIA_ACCESS_ROLES`): Admin, Fin, Sales, AM. */
  canReadInvoices: boolean;
  /** ДЗ студента (`studentHomeworkForErp`, `HOMEWORK_ERP_ROLES`): Admin, AM, Methodist. */
  canReadHomework: boolean;

  /* ---------- вкладки (по главному запросу вкладки) ---------- */

  /**
   * ТЗ 0120 «Портрет» — ИИ-портрет (`aiStudentPortrait`): роли портрета
   * (`AI_PORTRAIT_ROLES` = `canSeePortrait` из `useAiAgentPermissions`).
   */
  canSeePortraitTab: boolean;
  /** «Договоры» — `agreements`: `MANAGEMENT_ROLES`. */
  canSeeAgreementsTab: boolean;
  /** «Взносы» — взносы договоров: `MANAGEMENT_ROLES`. */
  canSeePaymentsTab: boolean;
  /** «Счета» — фактуры и проформы: `FAKTUROWNIA_ACCESS_ROLES`. */
  canSeeInvoicesTab: boolean;
  /** «Документы на подпись» — `PANDADOC_ACCESS_ROLES`. */
  canSeePandadocTab: boolean;
  /** «Домашние задания» — `HOMEWORK_ERP_ROLES`. */
  canSeeHomeworkTab: boolean;
  /** «Опросы» — `studentSurveyResponses`. */
  canSeeSurveysTab: boolean;
  /** «Общение» — Slack, переписка, заметки, SMS: `MANAGEMENT_ROLES`. */
  canSeeCommunicationTab: boolean;
  /** «Задачи» — `staffTasks`: `MANAGEMENT_ROLES`. */
  canSeeTasksTab: boolean;
}

/** Флаги видимости вкладок — из них `visibleStudentTabs` строит полосу. */
export type IStudentTabFlags = Pick<
  IStudentPermissions,
  | 'canSeePortraitTab'
  | 'canSeeAgreementsTab'
  | 'canSeePaymentsTab'
  | 'canSeeInvoicesTab'
  | 'canSeePandadocTab'
  | 'canSeeHomeworkTab'
  | 'canSeeSurveysTab'
  | 'canSeeCommunicationTab'
  | 'canSeeTasksTab'
>;

/**
 * Вкладки карточки студента, доступные роли, в порядке `tabs` (канонический
 * порядок — `StudentDetail/tabIds`). «Обзор» виден всем, кому открыта
 * карточка (виджеты внутри — по своим флагам). Вкладка, скрытая от роли, при
 * заходе по адресу (`?tab=payments`) откатывается на «Обзор» — это делает
 * страница, выбирая активную вкладку ИЗ этого списка.
 */
export const visibleStudentTabs = <T extends StudentTabId>(
  tabs: readonly T[],
  flags: IStudentTabFlags,
): T[] => {
  const gate: Record<StudentTabId, boolean> = {
    overview: true,
    portrait: flags.canSeePortraitTab,
    agreements: flags.canSeeAgreementsTab,
    payments: flags.canSeePaymentsTab,
    invoices: flags.canSeeInvoicesTab,
    pandadoc: flags.canSeePandadocTab,
    homework: flags.canSeeHomeworkTab,
    surveys: flags.canSeeSurveysTab,
    communication: flags.canSeeCommunicationTab,
    tasks: flags.canSeeTasksTab,
  };
  return tabs.filter((tabId) => gate[tabId]);
};

const hasAnyRole = (roles: UserRole[], allowed: readonly UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/**
 * Права карточки студента для набора ролей — чистая функция, чтобы матрицу
 * «роль × вкладка/блок» можно было проверить без React.
 */
export const studentPermissionsFor = (
  roles: UserRole[],
): IStudentPermissions => {
  const canViewFinance = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  const canRecordPayment = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
  ]);

  const canManageRelationship = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  // Block LMS access — same set the backend accepts (PAYMENT_ACCESS_ROLES:
  // Admin, FinanceManager, AccountManager, SalesManager). ТЗ 0093: the same
  // constant gates the «Робот блокировки должников» settings section.
  const canBlockLmsAccess = hasAnyRole(roles, PAYMENT_ACCESS_ROLES);

  // `studentSurveyResponses` — the survey owners (SURVEY_MANAGER_ROLES) plus
  // HRManager, the insights read tier (useSurveyPermissions.canReadInsights).
  const canReadStudentSurveys = hasAnyRole(
    roles,
    STUDENT_SURVEY_RESPONSES_ROLES,
  );

  const readsStaffFields = hasAnyRole(roles, STUDENT_STAFF_FIELDS_ROLES);
  const canReadInvoices = hasAnyRole(roles, STUDENT_INVOICES_ROLES);
  const canReadHomework = hasAnyRole(roles, STUDENT_HOMEWORK_ROLES);

  return {
    canViewFinance,
    canExportFinance: canViewFinance,
    canRecordPayment,
    canIssueInvoice: canRecordPayment,
    canLinkInvoice: hasAnyRole(roles, INVOICE_LINK_ROLES),
    canIssueFaktura: canRecordPayment,
    canAddAgreement: canManageRelationship,
    canChangeGroup: canManageRelationship,
    canContactEmail: canManageRelationship,
    canBlockLmsAccess,
    canReadStudentSurveys,
    canCorrectAgreementGroup: hasAnyCorrectionRole(
      roles,
      AGREEMENT_CORRECT_ROLES,
    ),
    canUndoEnrollment: hasAnyCorrectionRole(roles, ENROLLMENT_UNDO_ROLES),
    canOpenStudentList: hasAnyRole(roles, STUDENT_ROLES),

    canReadMoney: readsStaffFields,
    canReadContacts: readsStaffFields,
    canReadOrigin: readsStaffFields,
    canReadTimeline: readsStaffFields,
    canReadInvoices,
    canReadHomework,

    canSeePortraitTab: hasAnyRole(roles, AI_PORTRAIT_ROLES),
    canSeeAgreementsTab: readsStaffFields,
    canSeePaymentsTab: readsStaffFields,
    canSeeInvoicesTab: canReadInvoices,
    canSeePandadocTab: hasAnyRole(roles, STUDENT_PANDADOC_ROLES),
    canSeeHomeworkTab: canReadHomework,
    canSeeSurveysTab: canReadStudentSurveys,
    canSeeCommunicationTab: readsStaffFields,
    canSeeTasksTab: readsStaffFields,
  };
};

/**
 * Reads `userStateVar().me.roles` and maps `UserRole` to capability booleans
 * for the Student surfaces (`studentPermissionsFor`).
 */
export const useStudentPermissions = (): IStudentPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  return studentPermissionsFor(roles);
};

/**
 * Есть ли у оператора маршрут карточки студента (`STUDENT_CARD_ROLES`). HR
 * видит студента в коммуникаторе, но карточки у него нет — ссылка дала бы 404.
 */
/**
 * Есть ли у оператора маршрут карточки счёта (`/finances/invoices/:id`) —
 * зеркало `fakturowniaInvoice` в `AppRouter`: Admin, AccountManager,
 * SalesManager, FinanceManager (`FINANCE_VIEW_ROLES`). Номер счёта у
 * остальных — текстом, не ссылкой в 404.
 */
export const useCanOpenInvoiceCard = (): boolean => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  return hasAnyRole(roles, FINANCE_VIEW_ROLES);
};

export const useCanOpenStudentCard = (): boolean => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  return hasAnyRole(roles, STUDENT_CARD_ROLES);
};
